import React, { useRef, useCallback } from 'react';

import { Modal, ModalFooter, ModalPrimaryBtn, ModalSecondaryBtn } from 'components/modal/Modal';
import { updateEnrollmentProgress } from 'common/api/crm/organization/enrollment';
import { ProgressState } from 'common/api/crm/enums/ProgressState';
import { withRetries } from 'common/utils/withRetries';
import { setBillingSelection, BillingSelection } from 'common/api/e-comm/payment-types';
import { LoadingOverlay, openLoader, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { useAlert } from 'modules/alerts';
import { SERVICE_UNAVAILABLE_MESSAGE } from 'config';

interface PaperOnlyModalProps {
    isOpen?: boolean;
    onClose: () => void;
    orgId: string;
    primaryBtnText: React.ReactNode;
    secondaryBtnText: React.ReactNode;
    onBtnPrimaryClick: () => void;
    onBtnSecondaryClick: () => void;
}

export const PaperOnlyModal = (props: PaperOnlyModalProps) => {
    const loader = useRef<LoadingOverlay>(null);
    const alert = useAlert();
    const { onBtnPrimaryClick } = props;

    const onContinueClick = useCallback(async () => {
        openLoader(loader.current);
        const result = await withRetries(setBillingSelection)(props.orgId, BillingSelection.Check);
        await closeLoader(loader.current);
        if (result.error) {
            alert({ message: SERVICE_UNAVAILABLE_MESSAGE });
            return;
        }
        withRetries(updateEnrollmentProgress)({
            state: ProgressState.PaperOnlySelected,
            organizationId: props.orgId,
        });
        onBtnPrimaryClick();
    }, [alert, props.orgId, onBtnPrimaryClick]);

    return (
        <Modal title="Link a bank account later" isOpen={props.isOpen} onClose={props.onClose}>
            <div className="text-grey-1 space-y-4">
                <p>
                    Don&apos;t have your organization&apos;s banking information right now? No
                    problem. You can still launch your fundraiser and start earning today.
                </p>
                <p>Please note:</p>
                <ul className="list-disc ml-6 leading-7 space-y-1.5">
                    <li>
                        <strong>Fewer Brands:</strong> Access to a smaller catalog of eGift cards
                        and reloadable gift cards
                    </li>
                    <li>
                        <strong>Quarterly vs. Monthly Payouts:</strong> You&apos;ll receive earnings
                        by check on a quarterly basis with a minimum earnings threshold of $20,
                        instead of monthly through automatic direct deposits with no minimum
                        threshold
                    </li>
                    <li>
                        <strong>Higher Fees:</strong> Participants pay $0.39 per order when using a
                        bank account for secure payment processing (discounted from $0.79 through
                        12/31/26)
                    </li>
                </ul>
                <p>
                    <strong>Need to set up a Bulk Order Program instead?</strong> This means
                    you&apos;ll collect payment from participants and send us a check for gift card
                    orders. This is a good choice for organizations without a designated bank
                    account (hospitals, public schools, etc). Please proceed by pressing &quot;Link
                    Bank Account Later&quot; below and mention &apos;Bulk Order Program&apos; when
                    connecting with a New Organization Growth Specialist.
                </p>
            </div>
            <ModalFooter>
                <ModalPrimaryBtn onClick={onContinueClick}>{props.primaryBtnText}</ModalPrimaryBtn>
                <ModalSecondaryBtn onClick={props.onBtnSecondaryClick}>
                    {props.secondaryBtnText}
                </ModalSecondaryBtn>
            </ModalFooter>
            <LoadingOverlay ref={loader} />
        </Modal>
    );
};
